/* Agatha redesign: home page (test theme). Owned by the home builder.
   - Height chart (agatha-hero): radiogroup with arrow keys, photo cross-fade, remembered age
     (Agatha.getAge / Agatha.setAge), live count on the age button through Agatha.count when the
     shared API is there (otherwise the label keeps no number).
   - New-in rail (agatha-new-in): loads the newest products with the Section Rendering API, then
     wires the previous / next buttons.
   Loaded on every page; does nothing when the home sections are absent. */
(() => {
  'use strict';

  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cfg = window.AgathaConfig || {};
  const locale = String(cfg.locale || document.documentElement.lang || 'en');
  const numFmt = (() => {
    try {
      return new Intl.NumberFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA');
    } catch (e) {
      return { format: String };
    }
  })();
  const api = () => window.Agatha || null;

  function readAge() {
    try {
      const a = api();
      if (a && typeof a.getAge === 'function') return a.getAge();
      const v = localStorage.getItem('agatha-age');
      return v ? v.replace(/"/g, '') : null;
    } catch (e) {
      return null;
    }
  }

  function saveAge(id) {
    try {
      const a = api();
      if (a && typeof a.setAge === 'function') a.setAge(id);
      else localStorage.setItem('agatha-age', id);
    } catch (e) {
      /* private mode: keep going */
    }
  }

  /* ---------- Height chart ---------- */
  function initHero(root) {
    if (root.dataset.agReady) return;
    root.dataset.agReady = '1';
    const group = root.querySelector('[role="radiogroup"]');
    const marks = Array.from(root.querySelectorAll('.ag-h-mark'));
    const shots = new Map(Array.from(root.querySelectorAll('.ag-h-shot')).map((el) => [el.dataset.age, el]));
    const panels = new Map(Array.from(root.querySelectorAll('.ag-h-panel')).map((el) => [el.dataset.age, el]));
    const cta = root.querySelector('[data-ag-hero-all]');
    const counts = new Map();
    const pending = new Map();
    if (!marks.length || !cta) return;

    let current = (marks.find((m) => m.getAttribute('aria-checked') === 'true') || marks[0]).dataset.age;

    const label = (id) => {
      const n = counts.get(id);
      // U+202F (narrow no-break space, fr) is missing from the web fonts: use U+00A0.
      return typeof n === 'number' && n > 1
        ? cta.dataset.labelN.replace('[n]', numFmt.format(n).replace(/\u202f/g, '\u00a0'))
        : cta.dataset.label;
    };

    function warm(id) {
      const shot = shots.get(id);
      if (shot) shot.querySelectorAll('img').forEach((i) => (i.loading = 'eager'));
      const panel = panels.get(id);
      if (panel) panel.querySelectorAll('img').forEach((i) => (i.loading = 'eager'));
    }

    function hideShot(el, animate) {
      el.classList.remove('is-active');
      if (!animate) {
        el.hidden = true;
        return;
      }
      window.setTimeout(() => {
        if (!el.classList.contains('is-active')) el.hidden = true;
      }, 480);
    }

    function swapShot(id, animate) {
      const next = shots.get(id);
      const prev = Array.from(shots.values()).filter((s) => s !== next && !s.hidden);
      if (!next) {
        prev.forEach((s) => hideShot(s, false));
        return;
      }
      if (!next.hidden && next.classList.contains('is-active')) return;
      next.hidden = false;
      const img = next.querySelector('img');
      let done = false;
      const reveal = () => {
        if (done) return;
        done = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (current !== id) return;
            next.classList.add('is-active');
            prev.forEach((s) => hideShot(s, animate));
          })
        );
      };
      if (!animate || !img || img.complete) {
        reveal();
      } else {
        img.loading = 'eager';
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
        window.setTimeout(reveal, 900);
      }
    }

    async function updateCount(id) {
      const a = api();
      if (counts.has(id) || pending.has(id) || !a || typeof a.count !== 'function' || typeof a.ageQuery !== 'function') return;
      const base = root.dataset.collectionUrl;
      if (!base) return;
      const job = (async () => {
        const q = await a.ageQuery(id);
        if (!q || !q.size) return;
        const n = await a.count(`${base}?${q.size}&filter.v.availability=1`);
        if (typeof n === 'number' && isFinite(n)) counts.set(id, n);
      })()
        .catch(() => {})
        .finally(() => {
          pending.delete(id);
          if (current === id) cta.textContent = label(id);
        });
      pending.set(id, job);
    }

    function select(id, { focus = false, animate = !reduce, remember = false } = {}) {
      const mark = marks.find((m) => m.dataset.age === id);
      if (!mark) return;
      current = id;
      marks.forEach((m) => {
        const on = m === mark;
        m.setAttribute('aria-checked', on ? 'true' : 'false');
        m.tabIndex = on ? 0 : -1;
      });
      if (focus) mark.focus();
      warm(id);
      panels.forEach((el, age) => (el.hidden = age !== id));
      swapShot(id, animate);
      cta.href = mark.dataset.href;
      cta.textContent = label(id);
      if (remember) saveAge(id);
      updateCount(id);
    }

    group.addEventListener('click', (e) => {
      const m = e.target.closest('.ag-h-mark');
      if (m) select(m.dataset.age, { remember: true });
    });
    group.addEventListener('keydown', (e) => {
      const i = marks.findIndex((m) => m.dataset.age === current);
      const step = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 }[e.key];
      let n;
      if (step !== undefined) n = Math.min(marks.length - 1, Math.max(0, i + step));
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = marks.length - 1;
      else return;
      e.preventDefault();
      select(marks[n].dataset.age, { focus: true, remember: true });
    });
    ['pointerenter', 'focusin', 'touchstart'].forEach((type) =>
      group.addEventListener(
        type,
        (e) => {
          const m = e.target.closest && e.target.closest('.ag-h-mark');
          if (m) warm(m.dataset.age);
        },
        { passive: true, capture: type === 'pointerenter' }
      )
    );

    const saved = readAge();
    if (saved && saved !== current && marks.some((m) => m.dataset.age === saved)) select(saved, { animate: false });
    else updateCount(current);
    // The shared API may finish loading after this file: retry the count once the page has loaded.
    window.addEventListener('load', () => updateCount(current), { once: true });
  }

  /* ---------- New-in rail ---------- */
  function wireRail(root) {
    const track = root.querySelector('[data-ag-rail-track]');
    const [prev, next] = root.querySelectorAll('.ag-h-rail__btn');
    if (!track || !prev || !next) return;
    const update = () => {
      prev.disabled = track.scrollLeft < 8;
      next.disabled = track.scrollLeft + track.clientWidth > track.scrollWidth - 8;
    };
    const go = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  async function initRail(root) {
    if (root.dataset.agReady) return;
    root.dataset.agReady = '1';
    const track = root.querySelector('[data-ag-rail-track]');
    const src = root.dataset.src;
    if (src && track) {
      try {
        const r = await fetch(src, { credentials: 'same-origin' });
        if (!r.ok) throw new Error(`new-in ${r.status}`);
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const fresh = doc.querySelector('[data-ag-rail-track]');
        if (!fresh || !fresh.querySelector('.ag-h-rail__item')) throw new Error('new-in: empty');
        track.innerHTML = fresh.innerHTML;
        track.removeAttribute('aria-busy');
      } catch (e) {
        root.hidden = true;
        return;
      }
    }
    wireRail(root);
  }

  function init() {
    document.querySelectorAll('[data-ag-hero]').forEach(initHero);
    document.querySelectorAll('[data-ag-newin]').forEach(initRail);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  // Theme editor: sections re-render in place.
  document.addEventListener('shopify:section:load', init);
})();
